import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  FaCheckCircle, 
  FaSchool, 
  FaUsers, 
  FaGraduationCap, 
  FaClipboardList, 
  FaChartLine,
  FaApple,
  FaGooglePlay 
} from "react-icons/fa";
import StructuredData from "../components/StructuredData";
import "../styles/landing-page.css";
import "../styles/app-download.css";
import "../styles/custom-accordion.css";

const SchoolManagementSystem = () => {
  // Structured data specific to school management system
  const schoolMgmtStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Pathshala Sanchalak School Management System",
    applicationCategory: "EducationalApplication, BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    description: "Comprehensive school management system to streamline administration, student management, faculty management, and academic operations.",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "152",
    },
    featureList: "Student Information System, Faculty Management, Attendance Tracking, Fee Management, Exam Management, Report Cards, Parent Portal",
  };

  // State to track which FAQ is open
  const [activeFaq, setActiveFaq] = useState("faq1");
  
  // Toggle function for FAQ items
  const toggleFaq = (id) => {
    setActiveFaq(activeFaq === id ? null : id);
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>School Management System Software - Free & Complete Solution | Pathshala Sanchalak</title>
        <meta 
          name="description" 
          content="India's best school management system software for effortless administration. Manage students, faculty, attendance, fees & more in one integrated platform. Start free now!" 
        />
        <meta
          name="keywords"
          content="school management system, school erp, school administration software, student information system, school management software, education management system"
        />
        <script type="application/ld+json">
          {JSON.stringify(schoolMgmtStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section text-center py-5">
        <div className="container">
          <h1 className="display-4 fw-bold mb-4">School Management System</h1>
          <p className="lead mb-4">
            Complete solution to streamline your school administration and enhance educational experience
          </p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-lg me-3">
              Start Free Trial
            </Link>
            <Link to="/#contact" className="btn btn-primary btn-lg">
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5 bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">Comprehensive School Management Features</h2>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaUsers className="feature-icon" />
                </div>
                <h3 className="feature-title">Student Management</h3>
                <p>
                  Complete student profiles, enrollment management, syllabus completion, and performance analytics all in one place.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Student admission & records</li>
                  <li><FaCheckCircle className="icon-check" /> Syllabus Completion Tracking</li>
                  <li><FaCheckCircle className="icon-check" /> Study Material availability</li>
                </ul>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaGraduationCap className="feature-icon" />
                </div>
                <h3 className="feature-title">Academic Management</h3>
                <p>
                  Streamline curriculum planning, timetable scheduling, exam management, and progress reporting.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Class & section management</li>
                  <li><FaCheckCircle className="icon-check" /> Timetable generation</li>
                  <li><FaCheckCircle className="icon-check" /> Exam & result management</li>
                </ul>
              </div>
            </div>
            {/* <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaClipboardList className="feature-icon" />
                </div>
                <h3 className="feature-title">Administrative Tools</h3>
                <p>
                  Handle fee management, inventory, transport, hostel, and other administrative functions efficiently.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Fee collection & tracking</li>
                  <li><FaCheckCircle className="icon-check" /> Expense management</li>
                  <li><FaCheckCircle className="icon-check" /> Transport & hostel management</li>
                </ul>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <div className="container">
          <h2 className="section-title text-center mb-5">Why Choose Our School Management System?</h2>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">100% Free Solution</h3>
                  <p>No hidden costs, no premium tiers - all features are completely free forever.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Easy Implementation</h3>
                  <p>Get up and running quickly with our intuitive interface and dedicated support.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Mobile Applications</h3>
                  <p>Access your school management system on the go with our dedicated mobile apps.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Data Security</h3>
                  <p>Enterprise-grade security to keep your institution's and students' data safe.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="app-download-section py-5">
        <div className="container">
          <div className="app-download-container">
            <div className="app-info">
              <h2>Download Our Mobile Apps</h2>
              <p>Access your school management system on the go with our dedicated mobile applications for students and faculty members.</p>
              
              <div className="app-category mt-4 mb-3">
                <h4>For Students: Chhaatr App</h4>
                <p>Students can access their timetable, study materials, assignments, and communicate with teachers.</p>
                <div className="app-store-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-chhaatr/id6745966365" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on App Store"
                  >
                    <FaApple /> App Store
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.bojack.chhaatrpathshalasanchalakapp&pcampaignid=web_share" 
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on Google Play"
                  >
                    <FaGooglePlay /> Google Play
                  </a>
                </div>
              </div>
              
              <div className="app-category mt-4">
                <h4>For Teachers: Adhyapak App</h4>
                <p>Teachers can manage attendance, update study materials, grade assignments, and communicate with students.</p>
                <div className="app-store-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-adhyapak/id6745964284" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on App Store"
                  >
                    <FaApple /> App Store
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.bojack.adhyapakpathshalasanchalakapp&pcampaignid=web_share" 
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on Google Play"
                  >
                    <FaGooglePlay /> Google Play
                  </a>
                </div>
              </div>
            </div>
            
            <div className="app-screenshots d-none d-md-flex">
              {/* <img 
                src="https://via.placeholder.com/300x600?text=Chhaatr+App" 
                alt="Chhaatr App Screenshot" 
                className="app-screenshot"
              />
              <img 
                src="https://via.placeholder.com/300x600?text=Adhyapak+App" 
                alt="Adhyapak App Screenshot" 
                className="app-screenshot second"
              /> */}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white text-center">
        <div className="container">
          <h2 className="mb-4">Ready to transform your school administration?</h2>
          <p className="lead mb-4">Start your journey with our all-in-one software built for education centers.</p>
          <Link to="/register" className="btn btn-primary btn-lg">
            Get Started for Free
          </Link>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="faq-section py-5 bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">Frequently Asked Questions</h2>
          <div className="custom-accordion">
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq1" ? "active" : ""}`}
                onClick={() => toggleFaq("faq1")}
              >
                What is a school management system?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq1" ? "show" : ""}`}>
                A school management system is a comprehensive software solution that helps educational institutions manage administrative, academic, and operational functions. It centralizes student information, attendance tracking, grade management, fee collection, and other essential school processes in one integrated platform.
              </div>
            </div>
            
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq2" ? "active" : ""}`}
                onClick={() => toggleFaq("faq2")}
              >
                Is Pathshala Sanchalak school management system really free?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq2" ? "show" : ""}`}>
                Yes, Pathshala Sanchalak offers a completely free school management system with all features included. There are no hidden costs, premium tiers, or time limitations. We believe in supporting educational institutions by providing high-quality software at no cost.
              </div>
            </div>
            
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq3" ? "active" : ""}`}
                onClick={() => toggleFaq("faq3")}
              >
                How can a school management system benefit my institution?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq3" ? "show" : ""}`}>
                A school management system can significantly improve efficiency by automating routine administrative tasks, reducing paperwork, enhancing communication between stakeholders, providing data-driven insights for decision-making, and creating a more organized educational environment. This allows school staff to focus more on teaching and student development rather than administrative burdens.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SchoolManagementSystem;
